import { useEffect, useMemo, useState } from 'react';
import { Building2, Check, Lock, LockOpen, Minus, Plus, Search, ShieldCheck, Trash2, UserRound, UsersRound } from 'lucide-react';
import type { EntradaPermiso, Nodo, Permisos, SujetoTipo } from '@doc/shared';
import { CLAVES_PERMISO, DESCRIPCION_PERMISO, ETIQUETA_PERMISO, SIN_PERMISOS, nivel, normalizar } from '@doc/shared';
import { Modal } from '../../ui/Modal.js';
import { useAvisos } from '../../ui/avisos.js';
import { useAcciones, usePermisosDeNodo, useSujetos } from './consultas.js';

interface Props {
  nodo: Nodo;
  alCerrar(): void;
}

interface Borrador {
  clave: string;
  sujetoTipo: SujetoTipo;
  sujetoId: string | null;
  sujetoNombre: string;
  sujetoDetalle: string | null;
  permisos: Permisos;
}

const claveDe = (tipo: SujetoTipo, id: string | null) => `${tipo}:${id ?? ''}`;

function IconoSujeto({ tipo }: { tipo: SujetoTipo }) {
  if (tipo === 'TODOS') return <Building2 size={15} style={{ color: 'var(--gold)' }} />;
  if (tipo === 'GRUPO') return <UsersRound size={15} style={{ color: 'var(--green-light)' }} />;
  return <UserRound size={15} style={{ color: 'var(--muted)' }} />;
}

export function DialogoPermisos({ nodo, alCerrar }: Props) {
  const { data, isLoading } = usePermisosDeNodo(nodo.id);
  const { guardarPermisos, cambiarHerencia } = useAcciones();
  const { avisar, avisarError } = useAvisos();

  const [borrador, setBorrador] = useState<Borrador[] | null>(null);
  const [agregando, setAgregando] = useState(false);

  // El borrador se arma una vez con lo que vino del servidor; después manda la
  // edición local hasta que se guarde o se cierre.
  useEffect(() => {
    if (!data || borrador) return;
    setBorrador(
      data.entradas
        .filter((e) => !e.heredada)
        .map((e) => ({
          clave: claveDe(e.sujetoTipo, e.sujetoId),
          sujetoTipo: e.sujetoTipo,
          sujetoId: e.sujetoId,
          sujetoNombre: e.sujetoNombre,
          sujetoDetalle: e.sujetoDetalle,
          permisos: e.permisos,
        })),
    );
  }, [data, borrador]);

  const heredadas = useMemo(() => data?.entradas.filter((e) => e.heredada) ?? [], [data]);
  const hereda = data?.nodo.heredaPermisos ?? nodo.heredaPermisos;

  const cambiar = (clave: string, capacidad: keyof Permisos, valor: boolean) =>
    setBorrador((previo) =>
      (previo ?? []).map((b) =>
        b.clave === clave ? { ...b, permisos: normalizar({ ...b.permisos, [capacidad]: valor }) } : b,
      ),
    );

  const agregar = (sujeto: Omit<Borrador, 'clave' | 'permisos'>) => {
    const clave = claveDe(sujeto.sujetoTipo, sujeto.sujetoId);
    setBorrador((previo) => {
      if ((previo ?? []).some((b) => b.clave === clave)) return previo;
      return [...(previo ?? []), { ...sujeto, clave, permisos: { ...SIN_PERMISOS, leer: true } }];
    });
    setAgregando(false);
  };

  async function guardar() {
    try {
      await guardarPermisos.mutateAsync({
        id: nodo.id,
        entradas: (borrador ?? []).map((b) => ({
          sujetoTipo: b.sujetoTipo,
          sujetoId: b.sujetoId,
          permisos: b.permisos,
        })),
      });
      avisar('Permisos guardados');
      alCerrar();
    } catch (error) {
      avisarError(error);
    }
  }

  async function alternarHerencia() {
    try {
      await cambiarHerencia.mutateAsync({ id: nodo.id, heredar: !hereda, copiarActuales: true });
      setBorrador(null); // se recarga con lo que quedó tras copiar la herencia
      avisar(hereda ? 'Esta carpeta ya no hereda permisos' : 'Volvió a heredar los permisos de la carpeta superior');
    } catch (error) {
      avisarError(error);
    }
  }

  return (
    <Modal
      titulo={`Permisos de "${nodo.nombre}"`}
      icono={<ShieldCheck size={17} style={{ color: 'var(--green-light)' }} />}
      ancho
      alCerrar={alCerrar}
      pie={
        <>
          <button className="btn" onClick={alCerrar}>
            Cancelar
          </button>
          <button className="btn btn--primario" onClick={() => void guardar()} disabled={guardarPermisos.isPending}>
            {guardarPermisos.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </>
      }
    >
      {isLoading && <div className="cargando">Cargando permisos…</div>}

      {!isLoading && (
        <>
          <div className={`herencia${hereda ? '' : ' cortada'}`}>
            {hereda ? <LockOpen size={16} style={{ color: 'var(--muted)' }} /> : <Lock size={16} style={{ color: 'var(--gold)' }} />}
            <div className="texto">
              {hereda ? (
                <>
                  <strong>Hereda los permisos</strong> de la carpeta superior. Los cambios que hagas arriba llegan
                  hasta acá.
                </>
              ) : (
                <>
                  <strong>Permisos propios.</strong> Lo que se otorgue en las carpetas superiores no aplica en esta
                  ni en lo que contenga.
                </>
              )}
            </div>
            {nodo.padreId && (
              <button className="btn btn--sm" onClick={() => void alternarHerencia()} disabled={cambiarHerencia.isPending}>
                {hereda ? 'Dejar de heredar' : 'Volver a heredar'}
              </button>
            )}
          </div>

          <table className="tabla-permisos">
            <thead>
              <tr>
                <th>Usuario o grupo</th>
                {CLAVES_PERMISO.map((c) => (
                  <th key={c} title={DESCRIPCION_PERMISO[c]}>
                    {ETIQUETA_PERMISO[c].split(' ')[0]}
                  </th>
                ))}
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {(borrador ?? []).map((b) => (
                <tr key={b.clave}>
                  <td>
                    <div className="sujeto">
                      <IconoSujeto tipo={b.sujetoTipo} />
                      <div>
                        <div className="sujeto__nombre">{b.sujetoNombre}</div>
                        <div className="sujeto__detalle">{b.sujetoDetalle ?? nivel(b.permisos)}</div>
                      </div>
                    </div>
                  </td>
                  {CLAVES_PERMISO.map((c) => (
                    <td key={c}>
                      <input
                        className="check"
                        type="checkbox"
                        checked={b.permisos[c]}
                        onChange={(e) => cambiar(b.clave, c, e.target.checked)}
                        aria-label={`${ETIQUETA_PERMISO[c]} para ${b.sujetoNombre}`}
                      />
                    </td>
                  ))}
                  <td>
                    <button
                      className="btn btn--fantasma btn--icono btn--sm"
                      title="Quitar acceso"
                      onClick={() => setBorrador((previo) => (previo ?? []).filter((x) => x.clave !== b.clave))}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}

              {heredadas.map((e) => (
                <FilaHeredada key={`${e.origenId}-${e.sujetoTipo}-${e.sujetoId}`} entrada={e} />
              ))}

              {(borrador ?? []).length === 0 && heredadas.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 18, textAlign: 'center' }}>
                    Nadie tiene acceso todavía, salvo los administradores.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ marginTop: 14 }}>
            {agregando ? (
              <SelectorSujeto yaAsignados={new Set((borrador ?? []).map((b) => b.clave))} alElegir={agregar} alCerrar={() => setAgregando(false)} />
            ) : (
              <button className="btn" onClick={() => setAgregando(true)}>
                <Plus size={14} /> Dar acceso a alguien
              </button>
            )}
          </div>

          <p className="muted" style={{ fontSize: 11.5, marginTop: 16, lineHeight: 1.55 }}>
            Los permisos se suman: alguien con acceso por grupo y por nombre propio termina con la unión de ambos. No
            existen denegaciones — para dejar a alguien fuera de una carpeta, córtale la herencia y otorga el acceso
            solo a quien corresponda.
          </p>
        </>
      )}
    </Modal>
  );
}

function FilaHeredada({ entrada }: { entrada: EntradaPermiso }) {
  return (
    <tr className="heredada">
      <td>
        <div className="sujeto">
          <IconoSujeto tipo={entrada.sujetoTipo} />
          <div>
            <div className="sujeto__nombre">{entrada.sujetoNombre}</div>
            <div className="sujeto__detalle">heredado de {entrada.origenNombre}</div>
          </div>
        </div>
      </td>
      {/* Marcas en vez de casillas: una casilla deshabilitada y marcada casi
          no se distingue de una vacía, y acá lo importante es leer de un
          vistazo qué llega heredado. */}
      {CLAVES_PERMISO.map((c) => (
        <td key={c}>
          {entrada.permisos[c] ? (
            <Check size={15} style={{ color: 'var(--green-light)' }} aria-label="otorgado" />
          ) : (
            <Minus size={15} style={{ color: 'var(--dim)' }} aria-label="no otorgado" />
          )}
        </td>
      ))}
      <td />
    </tr>
  );
}

interface PropsSelector {
  yaAsignados: Set<string>;
  alElegir(sujeto: { sujetoTipo: SujetoTipo; sujetoId: string | null; sujetoNombre: string; sujetoDetalle: string | null }): void;
  alCerrar(): void;
}

function SelectorSujeto({ yaAsignados, alElegir, alCerrar }: PropsSelector) {
  const { data } = useSujetos(true);
  const [texto, setTexto] = useState('');

  const filtro = texto.trim().toLowerCase();
  const coincide = (valor: string) => !filtro || valor.toLowerCase().includes(filtro);

  const opciones = [
    ...(coincide('toda la organización') && !yaAsignados.has('TODOS:')
      ? [{ sujetoTipo: 'TODOS' as SujetoTipo, sujetoId: null, sujetoNombre: 'Toda la organización', sujetoDetalle: 'Cualquier persona con cuenta activa' }]
      : []),
    ...(data?.grupos ?? [])
      .filter((g) => coincide(g.nombre) && !yaAsignados.has(`GRUPO:${g.id}`))
      .map((g) => ({
        sujetoTipo: 'GRUPO' as SujetoTipo,
        sujetoId: g.id,
        sujetoNombre: g.nombre,
        sujetoDetalle: `${g.miembros} miembro(s)`,
      })),
    ...(data?.usuarios ?? [])
      .filter((u) => (coincide(u.nombre) || coincide(u.email)) && !yaAsignados.has(`USUARIO:${u.id}`))
      .map((u) => ({
        sujetoTipo: 'USUARIO' as SujetoTipo,
        sujetoId: u.id,
        sujetoNombre: u.nombre,
        sujetoDetalle: u.email,
      })),
  ];

  return (
    <div className="selector-sujeto">
      <div className="buscador">
        <Search size={14} />
        <input
          className="input"
          style={{ paddingLeft: 30 }}
          placeholder="Buscar persona o grupo…"
          autoFocus
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && alCerrar()}
        />
      </div>
      <div className="selector-sujeto__lista">
        {opciones.length === 0 && <div style={{ padding: 12 }} className="muted">Sin coincidencias</div>}
        {opciones.map((o) => (
          <button key={claveDe(o.sujetoTipo, o.sujetoId)} className="selector-sujeto__item" onClick={() => alElegir(o)}>
            <IconoSujeto tipo={o.sujetoTipo} />
            <div>
              <div>{o.sujetoNombre}</div>
              <div className="sujeto__detalle">{o.sujetoDetalle}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
